import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Tag,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Input,
  Tooltip,
  Modal
} from 'antd';
import {
  ReloadOutlined,
  StopOutlined,
  DesktopOutlined,
  MobileOutlined,
  AppleOutlined,
  WindowsOutlined,
  AndroidOutlined,
  GlobalOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const { Search } = Input;

const SessionManagement = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

  useEffect(() => {
    fetchSessions();
    fetchStats();
  }, [pagination.current, pagination.pageSize]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.current,
        pageSize: pagination.pageSize
      });

      if (searchText) {
        params.append('username', searchText);
      }

      const response = await axios.get(`${API_BASE}/sessions/active?${params}`, {
        headers: AuthService.getAuthHeaders()
      });

      if (response.data.success) {
        setSessions(response.data.data.list);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.total
        }));
      }
    } catch (error) {
      message.error('获取会话列表失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/sessions/stats`, {
        headers: AuthService.getAuthHeaders()
      });

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('获取统计信息失败:', error);
    }
  };

  const handleTerminateSession = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API_BASE}/sessions/${sessionId}/terminate`,
        { reason: '管理员强制退出' },
        { headers: AuthService.getAuthHeaders() }
      );

      if (response.data.success) {
        message.success('会话已终止');
        fetchSessions();
        fetchStats();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '终止会话失败');
    }
  };

  const handleTerminateUserSessions = async (userId, username) => {
    Modal.confirm({
      title: '确认强制退出',
      icon: <ExclamationCircleOutlined />,
      content: `确定要强制退出用户「${username}」的所有会话吗？该用户需要重新登录才能继续使用系统。`,
      okText: '确定退出',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await axios.post(
            `${API_BASE}/sessions/user/${userId}/terminate`,
            { reason: '管理员强制退出所有会话' },
            { headers: AuthService.getAuthHeaders() }
          );

          if (response.data.success) {
            message.success(response.data.message);
            fetchSessions();
            fetchStats();
          }
        } catch (error) {
          message.error(error.response?.data?.message || '终止会话失败');
        }
      }
    });
  };

  const getDeviceIcon = (deviceInfo) => {
    if (!deviceInfo) return <DesktopOutlined />;
    if (deviceInfo.includes('iPhone') || deviceInfo.includes('iPad')) return <AppleOutlined />;
    if (deviceInfo.includes('Android')) return <AndroidOutlined />;
    if (deviceInfo.includes('Windows')) return <WindowsOutlined />;
    if (deviceInfo.includes('Mac')) return <AppleOutlined />;
    if (deviceInfo.includes('移动') || deviceInfo.includes('Mobile')) return <MobileOutlined />;
    return <DesktopOutlined />;
  };

  const getRoleTag = (role) => {
    const roleMap = {
      admin: { color: 'red', text: '管理员' },
      operations: { color: 'blue', text: '运营' },
      salesperson: { color: 'green', text: '销售' }
    };
    const config = roleMap[role] || { color: 'default', text: role };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '用户',
      key: 'user',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>
            <UserOutlined style={{ marginRight: 4 }} />
            {record.name}
          </span>
          <span style={{ fontSize: 12, color: '#666' }}>@{record.username}</span>
        </Space>
      )
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 80,
      render: (role) => getRoleTag(role)
    },
    {
      title: 'IP地址',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 180,
      render: (ip, record) => (
        <Tooltip title="点击复制IP">
          <Space direction="vertical" size={0}>
            <span
              style={{ cursor: 'pointer', fontFamily: 'monospace' }}
              onClick={() => {
                navigator.clipboard.writeText(ip);
                message.success('已复制IP地址');
              }}
            >
              <GlobalOutlined style={{ marginRight: 4 }} />
              {ip}
            </span>
            {record.ipLocation && (
              <span style={{ fontSize: 12, color: '#666' }}>
                {record.ipLocation}
              </span>
            )}
          </Space>
        </Tooltip>
      )
    },
    {
      title: '设备',
      dataIndex: 'deviceInfo',
      key: 'deviceInfo',
      width: 180,
      render: (device) => {
        if (!device) return <span style={{ color: '#999' }}>未知</span>;
        // 解析设备信息，格式通常是 "设备类型 / 操作系统 / 浏览器"
        const parts = device.split(' / ');
        const deviceType = parts[0] || '未知';
        const osAndBrowser = parts.slice(1).join(' / ');
        return (
          <Space direction="vertical" size={0}>
            <Space>
              {getDeviceIcon(device)}
              <span style={{ fontWeight: 500 }}>{deviceType}</span>
            </Space>
            {osAndBrowser && (
              <span style={{ fontSize: 12, color: '#666', marginLeft: 18 }}>
                {osAndBrowser}
              </span>
            )}
          </Space>
        );
      }
    },
    {
      title: '登录时间',
      dataIndex: 'loginAt',
      key: 'loginAt',
      width: 140,
      render: (time) => (
        <Tooltip title={dayjs(time).format('YYYY-MM-DD HH:mm:ss')}>
          <span>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {dayjs(time).fromNow()}
          </span>
        </Tooltip>
      )
    },
    {
      title: '最后活跃',
      dataIndex: 'lastActiveAt',
      key: 'lastActiveAt',
      width: 120,
      render: (time) =>
        time ? (
          <Tooltip title={dayjs(time).format('YYYY-MM-DD HH:mm:ss')}>
            {dayjs(time).fromNow()}
          </Tooltip>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        )
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Popconfirm
            title="确定要强制退出该会话吗？"
            description="用户需要重新登录才能继续使用"
            onConfirm={() => handleTerminateSession(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" danger icon={<StopOutlined />}>
              退出
            </Button>
          </Popconfirm>
          <Tooltip title="退出该用户所有会话">
            <Button
              size="small"
              danger
              type="dashed"
              onClick={() => handleTerminateUserSessions(record.userId, record.username)}
            >
              全部退出
            </Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    setTimeout(() => fetchSessions(), 0);
  };

  const handleTableChange = (pag) => {
    setPagination({
      ...pagination,
      current: pag.current,
      pageSize: pag.pageSize
    });
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* 统计卡片 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="在线用户"
                value={stats.onlineUsers}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="活跃会话"
                value={stats.activeSessions}
                prefix={<DesktopOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="管理员在线"
                value={stats.byRole?.find(r => r.role === 'admin')?.count || 0}
                valueStyle={{ color: '#f5222d' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="移动端会话"
                value={
                  stats.byDevice?.filter(d =>
                    ['iPhone', 'Android', 'iPad', '移动设备'].includes(d.device)
                  ).reduce((sum, d) => sum + d.count, 0) || 0
                }
                prefix={<MobileOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 会话列表 */}
      <Card
        title="活跃会话管理"
        extra={
          <Space>
            <Search
              placeholder="搜索用户名"
              allowClear
              onSearch={handleSearch}
              style={{ width: 200 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => { fetchSessions(); fetchStats(); }}>
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={sessions}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          onChange={handleTableChange}
          scroll={{ x: 1100 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default SessionManagement;
